import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Link, useNavigate } from "react-router-dom";
import { SignedIn, SignedOut, useUser } from "@clerk/clerk-react";
import { Switch } from "@/components/ui/switch";
import PDFShowcase from './PDFShowcase';
import HeroDog from './HeroDog';
import EnhancedFeaturesSection from './EnhancedFeaturesSection';
import DemoSection from './DemoSection';
import EnhancedShowcase from './EnhancedShowcase';
import DemoAnalyzer from './DemoAnalyzer';
import {
  Heart,
  Shield,
  Award,
  ArrowRight,
  Star,
  Camera,
  FileText,
  PawPrint,
  Clock,
  AlertCircle,
  Check,
  X,
  ChevronDown,
  Sparkles,
  Zap,
  Dog,
  Bone,
  Scan,
  Brain,
  Stethoscope,
  BarChart,
  Smartphone,
  Users,
  BadgeCheck,
  Gift,
  BellRing,
  Lock
} from 'lucide-react';

import VetsSection from './VetsSection';
import FinalCTA from './FinalCTA';
import Footer from './Footer';
import PricingSection from './PricingSection';
import FAQSection from './FAQSection';
import TestimonialsSection from './TestimonialsSection';
import RecentDogsGallery from '../components/RecentDogsGallery';

// Live Counter Component
const LiveCounter = () => {
  const [users, setUsers] = useState(8463);
  const [analyses, setAnalyses] = useState(24718);
  
  useEffect(() => {
    // Simulate random increases
    const userInterval = setInterval(() => {
      setUsers(prev => prev + Math.floor(Math.random() * 3) + 1);
    }, 12000);
    
    const analysesInterval = setInterval(() => {
      setAnalyses(prev => prev + Math.floor(Math.random() * 5) + 1);
    }, 5000);
    
    return () => {
      clearInterval(userInterval);
      clearInterval(analysesInterval);
    };
  }, []);
  
  return (
    <div className="fixed bottom-4 left-4 z-40 bg-white/90 backdrop-blur-sm rounded-lg shadow-lg border border-amber-200 overflow-hidden">
      <div className="px-4 py-2 flex flex-col sm:flex-row items-center gap-4">
        <div className="flex items-center gap-2">
          <div className="relative">
            <Users className="h-5 w-5 text-blue-600" />
            <span className="absolute -top-1 -right-1 w-2 h-2 bg-green-500 rounded-full animate-ping"></span>
          </div>
          <div>
            <div className="text-xs text-gray-500">Usuarios activos</div>
            <div className="font-bold text-blue-900">{users.toLocaleString()}</div>
          </div>
        </div>
        
        <div className="hidden sm:block w-px h-10 bg-gray-200"></div>
        
        <div className="flex items-center gap-2">
          <div className="relative">
            <Dog className="h-5 w-5 text-amber-600" />
            <span className="absolute -top-1 -right-1 w-2 h-2 bg-green-500 rounded-full animate-ping"></span>
          </div>
          <div>
            <div className="text-xs text-gray-500">Análisis realizados</div>
            <div className="font-bold text-amber-900">{analyses.toLocaleString()}</div>
          </div>
        </div>
      </div>
      
      <div className="px-2 py-1.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs">
        <div className="flex justify-between items-center">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, repeat: Infinity, repeatType: "reverse" }}
          >
            ¡+317 nuevos usuarios hoy!
          </motion.div>
        </div>
      </div>
    </div>
  );
};

// Activity Notification Component
const ActivityNotification = () => {
  const [visible, setVisible] = useState(false);
  const [notification, setNotification] = useState({});
  
  const notifications = [
    { name: "Laura", location: "CDMX", action: "se registró hace 2 minutos", icon: Users, color: "green" },
    { name: "Carlos", location: "Monterrey", action: "analizó a su Labrador", icon: Dog, color: "amber" },
    { name: "Mariana", location: "Guadalajara", action: "consiguió un descuento", icon: Gift, color: "blue" },
    { name: "Alejandro", location: "Puebla", action: "compartió su análisis", icon: FileText, color: "purple" },
    { name: "Sofía", location: "Tijuana", action: "acaba de registrarse", icon: Users, color: "green" },
    { name: "Daniel", location: "Cancún", action: "analizó a su Chihuahua", icon: Dog, color: "amber" },
    { name: "Isabel", location: "Querétaro", action: "recibió su primer reporte", icon: FileText, color: "blue" },
    { name: "Javier", location: "León", action: "se registró hace 1 minuto", icon: Users, color: "green" },
  ];
  
  useEffect(() => {
    // Show notification after initial delay
    const initialDelay = setTimeout(() => {
      showRandomNotification();
    }, 5000);
    
    return () => clearTimeout(initialDelay);
  }, []);
  
  const showRandomNotification = () => {
    const randomNotif = notifications[Math.floor(Math.random() * notifications.length)];
    setNotification(randomNotif);
    setVisible(true);
    
    // Hide after 4 seconds and schedule next
    setTimeout(() => {
      setVisible(false);
      
      // Schedule next notification
      setTimeout(() => {
        showRandomNotification();
      }, Math.random() * 15000 + 20000); // Random interval between 20-35 seconds
    }, 4000);
  };
  
  return (
    <AnimatePresence mode="sync">
      {visible && (
        <motion.div 
          initial={{ x: -100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: -100, opacity: 0 }}
          transition={{ type: "spring", damping: 20 }}
          className="fixed bottom-32 left-4 bg-white rounded-lg shadow-lg z-50 p-3 border-l-4 border-green-500 max-w-[280px]"
        >
          <div className="flex items-center gap-2">
            <div className={`bg-${notification.color}-100 p-2 rounded-full`}>
              {notification.icon && <notification.icon className={`h-4 w-4 text-${notification.color}-600`} />}
            </div>
            <div>
              <p className="text-sm">
                <span className="font-medium">{notification.name}</span> de {notification.location} {notification.action}
              </p>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Animated paw print component
const AnimatedPawPrint = ({ delay, size, position, rotation }) => (
  <motion.div
    className={`absolute ${position} opacity-0`}
    initial={{ opacity: 0, scale: 0.5 }}
    animate={{ 
      opacity: [0, 0.03, 0.03, 0], 
      scale: [0.5, 1, 1, 0.8],
      y: [0, -20, -20, -40]
    }}
    transition={{ 
      duration: 4,
      delay: delay,
      repeat: Infinity,
      repeatDelay: 15
    }}
  >
    <PawPrint className={`${size} transform ${rotation}`} />
  </motion.div>
);

// Floating badge component
const FloatingBadge = ({ icon: Icon, text, color, delay, position }) => (
  <motion.div
    className={`absolute ${position} hidden lg:flex items-center gap-2 ${color} px-3 py-1.5 rounded-full shadow-lg backdrop-blur-sm border border-white/20 z-10`}
    initial={{ opacity: 0, y: 20 }}
    animate={{ 
      opacity: 1, 
      y: 0,
      x: [0, 10, 0, -10, 0],
      y: [0, -5, 0, 5, 0]
    }}
    transition={{ 
      delay: delay,
      duration: 10,
      repeat: Infinity,
      repeatType: "reverse"
    }}
  >
    <Icon className="h-4 w-4" />
    <span className="text-sm font-medium whitespace-nowrap">{text}</span>
  </motion.div>
);

// Play icon component
const Play = ({ className }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round" 
    className={className}
  >
    <polygon points="5 3 19 12 5 21 5 3"></polygon>
  </svg>
);

// Floating SocialProof component
const SocialProofBadge = () => {
  const [visible, setVisible] = useState(true);
  const [localCity, setLocalCity] = useState('tu ciudad');
  const [localCount, setLocalCount] = useState(342);
  
  useEffect(() => {
    // Simulate getting city from geolocation (in a real app, use actual geolocation)
    const cities = ['Ciudad de México', 'Monterrey', 'Guadalajara', 'Puebla', 'Tijuana', 'Cancún'];
    setLocalCity(cities[Math.floor(Math.random() * cities.length)]);
    
    // Randomize local count
    setLocalCount(Math.floor(Math.random() * 200) + 250);
    
    // Toggle visibility periodically for attention
    const interval = setInterval(() => {
      setVisible(prev => !prev);
    }, 5000);
    
    return () => clearInterval(interval);
  }, []);
  
  return (
    <AnimatePresence mode="sync">
      {visible && (
        <motion.div 
          className="fixed bottom-32 right-4 z-40 bg-white rounded-lg shadow-lg border border-green-200 p-3 max-w-[250px]"
          initial={{ x: 100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: 100, opacity: 0 }}
          transition={{ type: "spring", damping: 20 }}
        >
          <div className="flex items-center gap-2 mb-2">
            <div className="bg-green-100 p-2 rounded-full">
              <Dog className="h-4 w-4 text-green-600" />
            </div>
            <div className="text-green-800 font-medium">Mascotas cerca de ti</div>
          </div>
          
          <p className="text-sm text-gray-600 mb-1">
            <span className="font-bold text-green-600">{localCount}</span> mascotas en {localCity} ya han sido analizadas
          </p>
          
          <div className="flex -space-x-2">
            {[1, 2, 3, 4, 5].map((num) => (
              <div key={num} className="w-6 h-6 rounded-full border-2 border-white overflow-hidden">
                <img 
                  src={`/avatars/pet${num}.jpg`} 
                  alt={`Pet ${num}`}
                  className="w-full h-full object-cover" 
                  onError={(e) => {e.target.src = '/demo-dogs/golden.jpg'}}
                />
              </div>
            ))}
            <div className="w-6 h-6 rounded-full bg-amber-500 border-2 border-white flex items-center justify-center text-[10px] text-white font-bold">
              +{Math.floor(localCount/10)}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Main Landing Page Component
const LandingPage = () => {
  const { isSignedIn, isLoaded } = useUser();
  const navigate = useNavigate();
  const [scrollY, setScrollY] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll();
  
  // Parallax effect values
  const y1 = useTransform(scrollYProgress, [0, 1], [0, -100]);
  const y2 = useTransform(scrollYProgress, [0, 1], [0, -200]);
  const opacity = useTransform(scrollYProgress, [0, 0.3], [1, 0]);

  useEffect(() => {
    if (isLoaded && isSignedIn) {
      navigate("/dog-analyzer", { replace: true });
    }
    
    const handleScroll = () => {
      setScrollY(window.scrollY);
      setShowScrollTop(window.scrollY > 500);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isLoaded, isSignedIn]);

  const handleGetStarted = () => {
    navigate("/?sign-in=true");
  };

  // Close mobile menu when clicking a navigation link
  const handleNavClick = () => {
    setMobileMenuOpen(false);
  };

  // Scroll to top function
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // Stats for the hero section
  const stats = [
    { icon: Scan, value: "90%", label: "Precisión en análisis" },
    { icon: Clock, value: "2min", label: "Resultados inmediatos" },
    { icon: Users, value: "10k+", label: "Mascotas analizadas" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 via-orange-50 to-white relative overflow-hidden">
      {/* Live Counter */}
      <LiveCounter />
      
      {/* Social Proof Badge */}
      <SocialProofBadge />
      
      {/* Animated Background Elements */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        {/* Dynamic paw print pattern */}
        <AnimatedPawPrint delay={0} size="w-[800px] h-[800px]" position="-right-[200px] -top-[200px]" rotation="rotate-12" />
        <AnimatedPawPrint delay={2} size="w-[600px] h-[600px]" position="-left-[100px] top-[30%]" rotation="-rotate-12" />
        <AnimatedPawPrint delay={4} size="w-[500px] h-[500px]" position="right-[10%] bottom-[10%]" rotation="rotate-45" />
        <AnimatedPawPrint delay={6} size="w-[300px] h-[300px]" position="left-[20%] top-[20%]" rotation="-rotate-6" />
        <AnimatedPawPrint delay={8} size="w-[250px] h-[250px]" position="right-[30%] top-[40%]" rotation="rotate-25" />
        
        {/* Gradient orbs */}
        <div className="absolute top-1/4 left-1/4 w-[300px] h-[300px] rounded-full bg-amber-300/10 blur-[100px]" />
        <div className="absolute bottom-1/3 right-1/3 w-[400px] h-[400px] rounded-full bg-orange-300/10 blur-[120px]" />
        <div className="absolute top-2/3 left-1/3 w-[350px] h-[350px] rounded-full bg-yellow-200/10 blur-[80px]" />
        
        {/* Visual cue to indicate content below */}
        <div className="absolute bottom-0 left-0 right-0 h-[20vh] bg-gradient-to-t from-white via-white/80 to-transparent" />
      </div>

      {/* Floating badges */}
      <FloatingBadge 
        icon={Sparkles} 
        text="Tecnología AI" 
        color="bg-amber-500/10 text-amber-700"
        delay={1}
        position="top-[20%] right-[10%]"
      />
      <FloatingBadge 
        icon={Shield} 
        text="Detección temprana" 
        color="bg-blue-500/10 text-blue-700"
        delay={2}
        position="top-[40%] left-[5%]"
      />
      <FloatingBadge 
        icon={Stethoscope} 
        text="Veterinarios 24/7" 
        color="bg-green-500/10 text-green-700"
        delay={3}
        position="bottom-[30%] right-[15%]"
      />

      {/* Main Content */}
      <div className="relative z-10">

        {/* Mobile Navigation Menu - Slide down when active */}
        <AnimatePresence mode="sync">
          {mobileMenuOpen && (
            <motion.div 
              className="md:hidden bg-white border-b border-amber-100 shadow-lg"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="px-4 py-3 space-y-1">
                {['Probarlo ahora', 'Características', 'Cómo funciona', 'Veterinarios', 'Planes'].map((item, index) => (
                  <motion.a 
                    key={index}
                    href={`#${['demo-analyzer', 'features', 'demo', 'vets', 'pricing'][index]}`}
                    className="block px-3 py-2 rounded-lg text-amber-900 font-medium hover:bg-amber-50"
                    onClick={handleNavClick}
                    whileHover={{ x: 5, backgroundColor: "rgba(251, 191, 36, 0.1)" }}
                  >
                    {item}
                  </motion.a>
                ))}
                <div className="pt-2 pb-1">
                  <SignedIn>
                    <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                      <Button 
                        onClick={() => navigate("/dog-analyzer")}
                        className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
                      >
                        Mi Dashboard
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </motion.div>
                  </SignedIn>
                  <SignedOut>
                    <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                      <Button 
                        onClick={handleGetStarted}
                        className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
                      >
                        Comenzar Ahora
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </motion.div>
                  </SignedOut>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Hero Section */}
        <section ref={heroRef} className="relative pt-10 pb-3 sm:pt-12 sm:pb-3 lg:pt-14 lg:pb-3 overflow-visible min-h-[65vh] lg:min-h-[60vh] flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full relative z-10">
            <div className="lg:grid lg:grid-cols-2 lg:gap-12 items-center">
              {/* Hero Content */}
              <motion.div 
                className="text-center lg:text-left mb-10 lg:mb-0"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                {/* Badge */}
                <motion.div 
                  className="inline-flex items-center gap-1.5 bg-amber-100 px-3 py-1 rounded-full mb-4 lg:mb-6"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5 }}
                  whileHover={{ scale: 1.05 }}
                >
                  <BadgeCheck className="h-4 w-4 text-amber-600" />
                  <span className="text-sm font-medium text-amber-800">Tecnología AI</span>
                </motion.div>

                {/* Headline */}
                <motion.h1 
                  className="text-4xl sm:text-5xl lg:text-5xl xl:text-6xl font-bold tracking-tight mb-3 lg:mb-4"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                >
                  <motion.span 
                    className="block text-amber-900"
                    initial={{ y: 20 }}
                    animate={{ y: 0 }}
                    transition={{ duration: 0.5, delay: 0.3 }}
                  >
                    Cuida la salud de
                  </motion.span>
                  <motion.span 
                    className="block bg-gradient-to-r from-amber-600 via-orange-500 to-amber-600 text-transparent bg-clip-text"
                    initial={{ y: 20 }}
                    animate={{ y: 0 }}
                    transition={{ duration: 0.5, delay: 0.4 }}
                  >
                    tu mejor amigo
                  </motion.span>
                </motion.h1>

                {/* Subheadline */}
                <motion.p 
                  className="text-lg text-amber-800/80 mb-6 lg:mb-8 max-w-xl mx-auto lg:mx-0"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.5 }}
                >
                  Analiza a tu mascota con inteligencia artificial y recibe información detallada sobre su salud, comportamiento y necesidades específicas.
                </motion.p>

                {/* CTA Buttons */}
                <motion.div 
                  className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.6 }}
                >
                  <motion.div
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                  >
                    <Button 
                      onClick={handleGetStarted}
                      className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white text-lg px-6 py-4 sm:px-8 sm:py-5 rounded-xl shadow-lg hover:shadow-xl transition-all"
                      size="lg"
                    >
                      Analizar a mi mascota
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </motion.div>
                  <motion.a 
                    href="#demo" 
                    className="inline-flex items-center justify-center gap-2 text-amber-700 hover:text-amber-900 font-medium px-6 py-4 sm:px-8 sm:py-5 bg-white/80 backdrop-blur-sm rounded-xl border border-amber-200 shadow-md hover:shadow-lg transition-all"
                    whileHover={{ scale: 1.03, backgroundColor: "rgba(255, 255, 255, 0.9)" }}
                    whileTap={{ scale: 0.97 }}
                  >
                    <Play className="h-5 w-5" />
                    Ver cómo funciona
                  </motion.a>
                </motion.div>

            
              </motion.div>

              {/* Hero Image */}
              <motion.div 
                className="relative"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <div className="relative mx-auto w-full max-w-md lg:max-w-none">
                  {/* Main Image Container */}
                  <div className="relative">
                    <motion.div 
                      className="relative z-10 rounded-2xl overflow-hidden shadow-xl border-2 border-white transform-gpu"
                      style={{ y: y1 }}
                      whileHover={{ 
                        scale: 1.02,
                        transition: { duration: 0.3 }
                      }}
                    >
                      {/* Subtle Glass Effect */}
                      <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent mix-blend-overlay pointer-events-none z-10" />
                      
                      {/* Main Image */}
                      <img 
                        src="/perro1.jpeg" 
                        alt="Dog being analyzed" 
                        className="w-full h-auto object-cover relative z-0"
                        style={{ maxHeight: "65vh", objectPosition: "center" }}
                      />
                      
                      {/* Enhanced Scan Effects */}
                      <motion.div 
                        className="absolute inset-0 bg-gradient-to-b from-transparent via-amber-500/20 to-transparent"
                        animate={{ 
                          y: ['-100%', '100%'],
                          opacity: [0, 0.8, 0]
                        }}
                        transition={{ 
                          duration: 2,
                          repeat: Infinity,
                          repeatType: "loop",
                          ease: "linear"
                        }}
                      />
                      
                      {/* Multiple Scan Lines - Reduced to 2 */}
                      {[0, 1].map((index) => (
                        <motion.div 
                          key={index}
                          className="absolute h-[1px] left-0 right-0"
                          style={{
                            background: `linear-gradient(to right, 
                              transparent, 
                              rgba(251, 191, 36, 0.4) 50%,
                              transparent
                            )`,
                            boxShadow: '0 0 8px rgba(251, 191, 36, 0.4)'
                          }}
                          animate={{ 
                            y: ['0%', '100%'],
                            opacity: [0, 1, 0]
                          }}
                          transition={{ 
                            duration: 2.5,
                            delay: index * 1.2,
                            repeat: Infinity,
                            repeatType: "loop",
                            ease: "linear"
                          }}
                        />
                      ))}
                      
                      {/* Grid Pattern */}
                      <div className="absolute inset-0 bg-[radial-gradient(rgba(255,255,255,0.15)_1px,transparent_1px)] bg-[size:20px_20px]" />
                      
                      {/* Corner Markers - Simplified */}
                      {['top-left', 'top-right', 'bottom-left', 'bottom-right'].map((position, index) => (
                        <motion.div
                          key={position}
                          className={`absolute ${position.includes('top') ? 'top-4' : 'bottom-4'} ${position.includes('left') ? 'left-4' : 'right-4'}`}
                        >
                          <motion.div
                            className="w-4 h-4 border-2 border-amber-400 rounded-lg"
                            animate={{
                              scale: [1, 1.2, 1],
                              opacity: [0.5, 1, 0.5],
                            }}
                            transition={{
                              duration: 2,
                              delay: index * 0.5,
                              repeat: Infinity,
                              ease: "easeInOut"
                            }}
                          />
                        </motion.div>
                      ))}

                      {/* Analysis Markers - Reduced to 2 most important */}
                      {[
                        { 
                          top: '25%', left: '25%', color: 'amber', size: '28px', delay: 0,
                          label: 'Análisis facial', value: '98% precisión'
                        },
                        { 
                          top: '65%', right: '30%', color: 'green', size: '28px', delay: 0.5,
                          label: 'Estado físico', value: 'Óptimo'
                        }
                      ].map((marker, index) => (
                        <motion.div 
                          key={index}
                          className={`absolute ${marker.top} ${marker.left} ${marker.right} group`}
                          style={{ width: marker.size, height: marker.size }}
                        >
                          {/* Marker Core */}
                          <motion.div
                            className={`absolute inset-0 rounded-full border-2 border-${marker.color}-500`}
                            animate={{ 
                              scale: [1, 1.4, 1],
                              opacity: [0.3, 0.8, 0.3]
                            }}
                            transition={{ 
                              duration: 2,
                              delay: marker.delay,
                              repeat: Infinity,
                              repeatType: "reverse"
                            }}
                          />
                          
                          {/* Center Dot */}
                          <motion.div 
                            className={`absolute inset-[35%] rounded-full bg-${marker.color}-500`}
                            animate={{ scale: [0.8, 1.2, 0.8] }}
                            transition={{ duration: 1, delay: marker.delay, repeat: Infinity }}
                          />

                          {/* Simple Tooltip */}
                          <div className="absolute opacity-0 group-hover:opacity-100 -translate-y-full -translate-x-1/2 top-0 left-1/2 bg-white px-2 py-1 rounded shadow-md text-xs whitespace-nowrap transition-opacity duration-200">
                            <div className="font-medium text-gray-900">{marker.label}</div>
                            <div className={`text-${marker.color}-600`}>{marker.value}</div>
                          </div>
                        </motion.div>
                      ))}
                    </motion.div>

                    {/* Floating UI Cards - Reduced to 3 most important */}
                    {[
                      {
                        position: '-right-4 top-1/4',
                        icon: Dog,
                        title: 'Raza detectada',
                        value: 'Chihuahua',
                        color: 'amber',
                        delay: 0.6
                      },
                      {
                        position: '-left-4 bottom-1/4',
                        icon: Shield,
                        title: 'Estado de salud',
                        value: 'Excelente',
                        color: 'green',
                        delay: 0.8
                      },
                      {
                        position: 'right-1/4 -bottom-4',
                        icon: Brain,
                        title: 'Comportamiento',
                        value: 'Activo y Juguetón',
                        color: 'blue',
                        delay: 1
                      }
                    ].map((card, index) => (
                      <motion.div 
                        key={index}
                        className={`absolute ${card.position} bg-white rounded-xl p-3 shadow-md z-20 
                                  border border-${card.color}-100 hover:bg-white/95`}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: card.delay, duration: 0.5 }}
                        style={{ y: y2 }}
                        whileHover={{ 
                          scale: 1.05,
                          y: y2.get() - 5,
                          boxShadow: "0 8px 16px -6px rgba(0, 0, 0, 0.1)"
                        }}
                      >
                        <div className="flex items-center gap-2">
                          <div className={`bg-${card.color}-100 p-1.5 rounded-lg`}>
                            <card.icon className={`h-5 w-5 text-${card.color}-600`} />
                          </div>
                          <div>
                            <div className="text-xs text-gray-500">{card.title}</div>
                            <div className={`text-sm font-bold text-${card.color === 'green' ? 'green-600' : 'gray-900'}`}>
                              {card.value}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                    
                    {/* Scan Circle Animation - Simplified to one circle */}
                    <div className="absolute inset-0 flex items-center justify-center z-20 pointer-events-none">
                      <motion.div 
                        className="absolute w-[200px] h-[200px] rounded-full border-2 border-dashed 
                                  border-amber-400/50 flex items-center justify-center"
                        animate={{ 
                          rotate: 360,
                          scale: [1, 1.05, 1]
                        }}
                        transition={{ 
                          rotate: { duration: 20, repeat: Infinity, ease: "linear" },
                          scale: { duration: 3, repeat: Infinity, ease: "easeInOut" }
                        }}
                      >
                        <motion.div 
                          className="absolute w-full h-[1px] bg-gradient-to-r from-transparent via-amber-400/50 to-transparent"
                          animate={{ rotate: [0, 360] }}
                          transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
                        />
                        <motion.div 
                          className="absolute h-full w-[1px] bg-gradient-to-b from-transparent via-amber-400/50 to-transparent"
                          animate={{ rotate: [0, -360] }}
                          transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
                        />
                      </motion.div>
                    </div>
                  </div>
                  
                  {/* Background Elements - Simplified */}
                  <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-amber-200 rounded-full blur-3xl opacity-30" />
                  <div className="absolute -top-6 -left-6 w-32 h-32 bg-orange-200 rounded-full blur-3xl opacity-30" />
                  
                  {/* Decorative Elements - Reduced to 2 */}
                  {[
                    { position: '-right-8 top-1/2', size: 'h-10 w-10', color: 'amber', icon: PawPrint, delay: 0 },
                    { position: '-left-6 bottom-1/4', size: 'h-8 w-8', color: 'orange', icon: Bone, delay: 1 }
                  ].map((element, index) => (
                    <motion.div 
                      key={index}
                      className={`absolute ${element.position} transform -translate-y-1/2`}
                      animate={{ 
                        y: [0, element.position.includes('top') ? -3 : 3, 0],
                        rotate: [0, element.position.includes('right') ? 5 : -5, 0],
                        opacity: [0.6, 0.8, 0.6]
                      }}
                      transition={{ 
                        duration: 4,
                        delay: element.delay,
                        repeat: Infinity,
                        ease: "easeInOut"
                      }}
                    >
                      <element.icon className={`${element.size} text-${element.color}-200`} />
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
          
          {/* Enhanced Scroll Indicator - Modified to better connect with gallery below */}
          <motion.div 
            className="absolute -bottom-5 left-1/2 transform -translate-x-1/2 flex flex-col items-center z-30"
            animate={{ 
              y: [0, 6, 0],
              opacity: [0.7, 1, 0.7]
            }}
            transition={{ 
              duration: 2,
              repeat: Infinity,
              repeatType: "reverse"
            }}
            whileHover={{ scale: 1.1 }}
          >
            <motion.div
              animate={{ y: [0, 4, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="bg-amber-500 rounded-full p-2 shadow-md"
            >
              <ChevronDown className="h-5 w-5 text-white" />
            </motion.div>
          </motion.div>
          
          {/* Visual connector to gallery */}
          <div className="absolute -bottom-12 left-0 right-0 h-28 bg-gradient-to-b from-orange-50/70 via-white/80 to-transparent pointer-events-none z-0"></div>
        </section>
        
        {/* Recent Scans Gallery Section - Modified to connect with hero */}
        <section className="relative mt-4 pb-12 sm:pb-16 bg-transparent overflow-visible">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-20">
            <div className="text-center mb-6">
              <motion.h2 
                className="text-2xl sm:text-3xl font-bold text-amber-900"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.6 }}
                transition={{ duration: 0.5 }}
              >
                Ver mascotas analizadas
              </motion.h2>
            </div>
            
            {/* Replace static gallery with dynamic RecentDogsGallery */}
            <RecentDogsGallery initialLimit={12} />
            
            <motion.div 
              className="mt-10 text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.5 }}
            >
              <SignedOut>
                <p className="text-amber-700 mb-4">
                  Analiza a tu propio perro o mascota y obtén resultados personalizados al instante.
                </p>
                <motion.div
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  className="inline-block"
                >
                  <Button 
                    onClick={handleGetStarted}
                    className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white px-6 py-3 rounded-xl shadow-md"
                  >
                    Analizar a mi mascota
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </motion.div>
              </SignedOut>
            </motion.div>
          </div>
        </section>

        {/* Demo Section */}
        <section id="demo" className="py-16 sm:py-24 bg-gradient-to-br from-white to-amber-50">
          <DemoSection />
        </section>

        {/* Vets Section */}
        <section id="vets" className="py-16 sm:py-24 bg-gradient-to-br from-amber-50 to-white">
          <VetsSection />
        </section>

        {/* Testimonials Section */}
        <section className="py-16 sm:py-24">
          <TestimonialsSection />
        </section>

        {/* Pricing Section */}
        <section id="pricing" className="py-16 sm:py-24 bg-gradient-to-br from-white to-amber-50">
          <PricingSection />
        </section>

        {/* FAQ Section */}
        <section className="py-16 sm:py-24">
          <FAQSection />
        </section>

        {/* Final CTA */}
        <section className="py-16 sm:py-24 bg-gradient-to-br from-amber-50 to-white">
          <FinalCTA />
        </section>

        {/* Footer */}
        <Footer />
      </div>

      {/* Scroll to top button */}
      <AnimatePresence mode="sync">
        {showScrollTop && (
          <motion.button
            className="fixed bottom-6 right-6 z-50 p-3 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-lg"
            onClick={scrollToTop}
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.5 }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            aria-label="Scroll to top"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
            </svg>
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LandingPage;